// The governed pricing CAMPAIGN strip + helpers, in their OWN module deliberately: the Landing
// page (the EAGER entry chunk, budgeted at 265 KB gz) mounts only this strip — importing it from
// pricingShared dragged PlanCard/matrix/offer machinery into the first paint and broke the bundle
// budget by exactly the margin this split removes. /pricing consumers keep their imports via
// pricingShared's re-exports.
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { cn, inrExactPaise } from "../../lib/utils";

// ── Governed pricing CAMPAIGN (launch/festival offer) ─────────────────────────────────────────
// Served in `pricing_integrity.campaign`, resolved SERVER-side (window on the school's IST clock,
// linked-coupon health, fail-closed inert shape). The client re-checks the calendar window as
// belt-and-braces only — a prerender snapshot or a tab hidden past the deadline must fail CLOSED
// to normal pricing rather than keep advertising an ended offer.
export function campaignFrom(pricingIntegrity) {
  const c = pricingIntegrity?.campaign;
  if (!c || c.active !== true) return null;
  // The window is IST calendar days (the server's clock) — comparing against the UTC date would
  // suppress a midnight-IST launch for its first 5½ hours and linger past the stated deadline.
  const today = new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
  if (c.ends_on && today > c.ends_on) return null;
  if (c.starts_on && today < c.starts_on) return null;
  return c;
}

// Mirrors backend `coupons.compute_discount` for the ONE coupon shape whose claim is exact on
// this page: PURE percent (no cap). The cards show GST-EXCLUSIVE prices ("GST is charged extra")
// while checkout applies the coupon to the GST-INCLUSIVE gross — a percentage scales both bases
// identically ((base×(1−p))×1.18 == (base×1.18)×(1−p)), but a FLAT amount or a bitten cap does
// not: subtracting ₹2,000 from the ex-GST sticker implies ₹360 less than the invoice actually
// charges at 18%. So flat/capped coupons get NO was/now card line (the strip and checkout still
// carry them honestly); the page never states a figure the invoice can contradict.
export function campaignDiscountPaise(campaign, planId, basePaise) {
  const coupon = campaign?.coupon;
  const base = Math.max(0, Number(basePaise) || 0);
  if (!coupon || base <= 0) return 0;
  if (coupon.kind !== "percent" || coupon.max_discount_paise) return 0;
  const applies = coupon.applies_to_plans || [];
  if (applies.length > 0 && !applies.includes(planId)) return 0;
  const d = Math.floor((base * (Number(coupon.value) || 0)) / 10000);
  return Math.max(0, Math.min(d, base));
}

const CAMPAIGN_THEME = {
  // Festive rides the sanctioned SECONDARY brand accent (orange) — sparing, never a warning
  // state; discount figures stay in the success greens like every other saving on this surface.
  festive: {
    strip: "border-orange-border bg-orange-soft",
    text: "text-orange-dark",
    // Solid orange-dark (#C2410C, 4.9:1 with white) — the AA-safe orange per the token rules;
    // the bright gradient is fills/icons-only vocabulary, and axe cannot even VERIFY text on a
    // gradient (it files it "incomplete"), so a gradient chip would be structurally unauditable.
    chip: "bg-orange-dark text-white vp-campaign-chip",
  },
  launch: {
    strip: "border-primary/25 bg-primary/5",
    text: "text-primary-700",
    chip: "bg-primary text-primary-fg vp-campaign-chip vp-campaign-chip-launch",
  },
};

export function campaignTheme(campaign) {
  return CAMPAIGN_THEME[campaign?.theme] || CAMPAIGN_THEME.festive;
}

// Exact-paise claim formatting lives in lib/utils (inrExactPaise) — money formatting is
// centralized there by contract (backend/tests/test_money_display.py). Re-exported for the
// existing consumers of this module.
export const inrCampaignExact = inrExactPaise;

export function campaignEndLabel(campaign) {
  if (!campaign?.show_end_date || !campaign?.ends_on) return "";
  const d = new Date(`${campaign.ends_on}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  const label = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return `Offer ends ${label}, 11:59 pm IST`;
}

// The slim in-section campaign strip: claim adjacent to the prices it modifies, never a sticky
// site-wide bar, never a countdown (a plain, real end date — false urgency is a named dark
// pattern under India's 2023 CCPA guidelines). Dismissal is remembered per campaign revision.
export function CampaignStrip({ campaign, cta = null, className = "" }) {
  const dismissKey = campaign ? `vp360_campaign_dismissed:${campaign.revision || `${campaign.name}:${campaign.ends_on}`}` : "";
  const [dismissed, setDismissed] = React.useState(() => {
    try { return dismissKey ? window.localStorage.getItem(dismissKey) === "1" : false; } catch { return false; }
  });
  // The campaign often arrives AFTER mount (the prerender bootstrap predates it; the background
  // revalidation delivers it without a remount), so the stored dismissal must be re-derived when
  // the key actually exists — the mount-time read alone would forget every dismissal on the
  // prerendered path, and could keep a stale dismissal across a mid-session campaign edit.
  React.useEffect(() => {
    try { setDismissed(dismissKey ? window.localStorage.getItem(dismissKey) === "1" : false); }
    catch { setDismissed(false); }
  }, [dismissKey]);
  if (!campaign || dismissed) return null;
  const theme = campaignTheme(campaign);
  const endLabel = campaignEndLabel(campaign);
  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(dismissKey, "1"); } catch { /* private mode — session-only dismissal */ }
  };
  return (
    <div
      role="status"
      data-testid="pricing-campaign-banner"
      className={cn("vp-campaign-strip relative overflow-hidden mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border px-4 py-3 sm:px-5", theme.strip, className)}
    >
      <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide whitespace-nowrap", theme.chip)}>
        <Sparkles className="vp-campaign-spark h-3 w-3" aria-hidden="true" />
        {campaign.badge || campaign.name}
      </span>
      <p className="min-w-0 flex-1 text-[13.5px] leading-snug text-ink">
        <strong className="font-semibold">{campaign.headline}</strong>
        {campaign.sub ? <span className="text-ink-2"> {campaign.sub}</span> : null}
        {campaign.coupon?.code ? (
          <span className="text-ink-2">
            {" "}Code <b className="font-semibold text-ink">{campaign.coupon.code}</b> is applied
            automatically at checkout{campaign.coupon.per_tenant_once ? " · one redemption per school" : ""}.
          </span>
        ) : campaign.trial_months > 0 ? (
          <span className="text-ink-2">
            {" "}Schools joining during the offer get a
            {" "}<b className="font-semibold text-ink">{campaign.trial_months}-month free trial</b> — no card needed.
          </span>
        ) : null}
        {endLabel ? <span className={cn("ml-1 whitespace-nowrap text-[12.5px] font-medium", theme.text)}>{endLabel}</span> : null}
      </p>
      {/* On surfaces where the prices are NOT directly below (the landing), the strip carries the
          visitor to them — a Persuade strip without a route to act is a dead end. */}
      {cta?.to && (
        <Link
          to={cta.to}
          data-testid="pricing-campaign-cta"
          className={cn("whitespace-nowrap text-[13px] font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring rounded-sm", theme.text)}
        >
          {cta.label || "See the offer"} →
        </Link>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss this offer"
        data-testid="pricing-campaign-dismiss"
        className="ml-auto grid h-7 w-7 shrink-0 place-items-center rounded-full text-ink-subtle transition-[color,background-color] duration-fast ease-premium hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

